import { useEffect, useState } from 'react';
import { DollarSign, ShoppingCart, Loader2, AlertCircle, Gamepad2 } from 'lucide-react';
import { revenueService } from '../services/revenueService';
import type { RevenueData, Sale } from '../types/api';
import { FILL_GRADIENT } from '../constants/gradients';

export default function RevenuePage() {
  const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
  const [sales, setSales] = useState<Sale[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRevenue();
  }, []);

  const fetchRevenue = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [revenueData, salesData] = await Promise.all([
        revenueService.getTotalRevenue(),
        revenueService.getSales()
      ]);
      setRevenueData(revenueData);
      setSales(salesData);
    } catch (error: any) {
      console.error('Failed to fetch revenue:', error);
      setError(error.message || 'Failed to load revenue data');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-12 h-12 animate-spin text-primary-500" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center py-24">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Failed to load revenue data</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={fetchRevenue}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const hasRevenue = revenueData && revenueData.total_revenue > 0;
  const hasSales = sales.length > 0;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Revenue Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Track your earnings from game sales
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Your Earnings */}
        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: FILL_GRADIENT, backgroundSize: '200% 200%', backgroundPosition: 'center' }}
            >
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
            ${revenueData?.creator_earnings.toFixed(2) || '0.00'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Your Earnings (70%)</div>
        </div>

        {/* Total Sales */}
        <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
            {revenueData?.total_sales || 0}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Sales</div>
        </div>
      </div>

      {/* Sales History Table */}
      <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border">
        <div className="p-6 border-b border-gray-200 dark:border-dark-border">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Sales History</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            All game purchases
          </p>
        </div>

        {/* Empty State */}
        {!hasSales && (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
              <ShoppingCart className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No sales yet</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-md">
              Set your game prices in the game settings to start earning revenue from sales.
            </p>
          </div>
        )}

        {/* Sales Table */}
        {hasSales && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Game
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Sale Price
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Your Earnings
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Date Sold
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {sales.map((sale) => (
                  <tr 
                    key={sale.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    {/* Game Info */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {sale.game_icon ? (
                          <img
                            src={sale.game_icon}
                            alt={sale.game_name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                            <Gamepad2 className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {sale.game_name}
                        </span>
                      </div>
                    </td>

                    {/* Sale Price */}
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        ${sale.amount.toFixed(2)}
                      </span>
                    </td>

                    {/* Creator Earnings */}
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm font-bold text-green-600 dark:text-green-400">
                        ${sale.creator_earnings.toFixed(2)}
                      </span>
                    </td>

                    {/* Date Sold */}
                    <td className="px-6 py-4 text-right">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(sale.purchased_at).toLocaleDateString()} {new Date(sale.purchased_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">
              About Revenue & Payouts
            </h4>
            <p className="text-sm text-blue-800 dark:text-blue-400">
              You earn 70% of each game sale. The 30% platform fee covers payment processing, hosting, and infrastructure costs. 
              Payout functionality is coming soon - for now, you can track your earnings here.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
